import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import type { Occurrence } from "./calendar";
import { isTauri, isTauriMobile } from "./platform";

/**
 * Client half of the Google Calendar link.
 *
 * The consent screen has to open in a real browser — Google rejects embedded
 * webviews — so the Tauri shells hand the URL to the system browser and wait for
 * the deep link back, exactly as Google sign-in does. On the web it is an
 * ordinary same-tab redirect.
 */

export type GoogleStatus = {
  /** False when the deployment has no Google OAuth client configured at all. */
  configured: boolean;
  connected: boolean;
  googleEmail?: string | null;
  connectedAt?: string | null;
};

export type GoogleEvent = {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  htmlLink: string | null;
  startsAt: string;
  endsAt: string;
  allDay: boolean;
};

/** Where Google should send the user once consent is done. */
function returnTo(): string {
  // The mobile shells are served from an internal origin a browser cannot reach,
  // so they come back through the same verified App Link as Google sign-in.
  if (isTauriMobile()) return "https://sebas.akhsanhakiki.com/app/oauth/google";
  if (isTauri()) return "sebas://oauth/google";
  return `${window.location.origin}/#/settings`;
}

export function useGoogleStatus() {
  return useQuery({
    queryKey: ["google", "status"],
    queryFn: () => api<GoogleStatus>("/v1/integrations/google/status"),
    staleTime: 60_000,
  });
}

export function useGoogleEvents(from: Date, to: Date, enabled: boolean) {
  return useQuery({
    queryKey: ["google", "events", from.toISOString(), to.toISOString()],
    enabled,
    // Someone else's calendar changes on its own schedule; a minute of staleness
    // keeps month-to-month paging from hammering Google's quota.
    staleTime: 60_000,
    queryFn: () =>
      api<{ connected: boolean; events: GoogleEvent[] }>(
        `/v1/integrations/google/events?from=${encodeURIComponent(
          from.toISOString(),
        )}&to=${encodeURIComponent(to.toISOString())}`,
      ),
  });
}

/**
 * Starts the consent flow.
 *
 * On the web this navigates away and never resolves. In the shells it resolves
 * once the deep link comes back, so the caller can refetch the status in place.
 */
export function useConnectGoogle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { url } = await api<{ url: string }>("/v1/integrations/google/connect", {
        method: "POST",
        body: JSON.stringify({ returnTo: returnTo() }),
      });

      if (!isTauri()) {
        window.location.href = url;
        // Give the navigation a tick rather than resolving into a dead render.
        await new Promise((r) => setTimeout(r, 5_000));
        return;
      }

      const [{ onOpenUrl }, { openUrl }] = await Promise.all([
        import("@tauri-apps/plugin-deep-link"),
        import("@tauri-apps/plugin-opener"),
      ]);

      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(
          () => reject(new Error("Timed out waiting for Google. Please try again.")),
          180_000,
        );
        // Registered before the browser opens so a fast return cannot outrun it.
        void onOpenUrl((urls) => {
          if (urls.some((u) => u.includes("google="))) {
            clearTimeout(timer);
            resolve();
          }
        });
        void openUrl(url).catch((err: unknown) => {
          clearTimeout(timer);
          const detail = err instanceof Error ? err.message : String(err);
          reject(new Error(`Could not open the browser: ${detail}`));
        });
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["google"] });
    },
  });
}

export function useDisconnectGoogle() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ ok: true }>("/v1/integrations/google", { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["google"] });
    },
  });
}

/**
 * Adapts Google events into the shape the calendar grid already renders.
 *
 * Google is asked for `singleEvents=true`, so recurrence is already expanded on
 * their side — every row here is one concrete occurrence and needs no RRULE
 * handling. The synthetic ids are prefixed so they can never collide with a
 * Sebas event id.
 */
export function googleToOccurrences(events: GoogleEvent[]): Occurrence[] {
  return events.map((e) => ({
    id: `google:${e.id}`,
    calendarId: "google",
    noteId: null,
    title: e.title,
    description: e.description,
    location: e.location,
    startsAt: e.startsAt,
    endsAt: e.endsAt,
    allDay: e.allDay,
    rrule: null,
    color: "lilac" as const,
    occurrenceKey: `google:${e.id}`,
    start: new Date(e.startsAt),
    end: new Date(e.endsAt),
    isRecurring: false,
    source: "google" as const,
    externalLink: e.htmlLink,
  }));
}

/** Human-readable outcome from the `?google=` param Google's callback appends. */
export const CONNECT_RESULTS: Record<string, { ok: boolean; message: string }> = {
  connected: { ok: true, message: "Google Calendar connected." },
  denied: { ok: false, message: "Google Calendar access was not granted." },
  no_refresh_token: {
    ok: false,
    message:
      "Google did not return a refresh token. Remove Sebas at myaccount.google.com/permissions, then connect again.",
  },
  error: { ok: false, message: "Could not finish connecting to Google. Please try again." },
};
